<template>
  <div class="problem-understanding">
    <ProblemInput
      ref="inputRef"
      v-model="rawQuery"
      placeholder="先简单描述你的技术问题，一句话也可以。"
      @change="handleInputChange"
      @submit="handleStartClick"
    >
      <template #actions>
        <slot name="actions-extra" />
      </template>
    </ProblemInput>

    <!-- 未输入（empty）时整条隐藏，只留输入框 placeholder -->
    <UnderstandingBar
      v-if="barState !== 'empty'"
      class="problem-understanding__bar"
      :state="barState"
      :sentence="barSentence"
    />

    <SuggestionChips
      v-if="showQuestions"
      class="problem-understanding__chips"
      :questions="questions"
      :answers="answers"
      @answer="handleAnswer"
      @finish="handleFinish"
      @skip="handleSkip"
    />

    <UnderstandingCard
      v-if="showCard"
      class="problem-understanding__card"
      :card="card"
      @start="handleStart"
      @revise="handleRevise"
      @quickStart="handleQuickStart"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import ProblemInput from './ProblemInput.vue'
import UnderstandingBar from './UnderstandingBar.vue'
import SuggestionChips from './SuggestionChips.vue'
import UnderstandingCard from './UnderstandingCard.vue'
import { understandProblem, streamUnderstanding, getQuestions } from '@/api/problemUnderstanding'

const MIN_LEN = 8

const props = defineProps({
  modelValue: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue', 'start', 'quickStart'])

const rawQuery = ref(props.modelValue)

// 外部 v-model 同步（如语音输入赋值）
watch(() => props.modelValue, (v) => {
  if (v !== rawQuery.value) rawQuery.value = v
})
watch(rawQuery, (v) => {
  emit('update:modelValue', v)
})
const understanding = ref(null) // 接口返回的完整对象

const loading = ref(false)
const showCard = ref(false)
const inputRef = ref(null)
const questions = ref([])   // 问题列表（≥2 个，必答）
const answers = ref({})     // 收集的答案 { field: value }
let requestSeq = 0 // 丢弃过期响应，防止旧请求覆盖新结果

// 状态机：empty → short（不足8字）→ typing → ready
const barState = computed(() => {
  if (!rawQuery.value) return 'empty'
  if (rawQuery.value.length < MIN_LEN) return 'short'
  if (loading.value) return 'typing'
  if (understanding.value) return 'ready'
  return 'typing'
})

// 有未答完的问题列表时显示
const showQuestions = computed(() =>
  questions.value.length > 0 && !showCard.value
)

// 所有问题是否都已作答（多选：至少选一个即算已答）
const allAnswered = computed(() =>
  questions.value.every((q) => {
    const v = answers.value[q.field]
    if (Array.isArray(v)) return v.length > 0
    return v !== undefined && v !== ''
  })
)

// 理解条文本：模型理解句（一次性完整显示，不滚动）
const barSentence = computed(() =>
  understanding.value?.understanding_sentence || ''
)

const card = computed(() => {
  if (understanding.value?.understanding_card) return understanding.value.understanding_card
  // 兜底卡：接口异常时也能出卡
  const f = understanding.value?.extracted_fields || {}
  return {
    standard_problem: rawQuery.value,
    search_directions: f.search_directions || [],
    uncertain_but_ok: []
  }
})

// 输入防抖后触发（<8 字不调接口）
function handleInputChange(value) {
  // 出卡后输入框被同步为标准问题，此时不做重新理解
  if (understanding.value?.understanding_card?.standard_problem === value) {
    return
  }
  if (!value || value.length < MIN_LEN) {
    understanding.value = null
    questions.value = []
    answers.value = {}
    showCard.value = false
    return
  }
  // 问题内容变化 → 立即失效旧理解（问题列表/卡马上消失），显示「正在理解」，等新结果
  showCard.value = false
  questions.value = []
  answers.value = {}
  understanding.value = null
  streamUnderstand({ raw_query: value })
}

// 理解 + 问题列表：并行两路（理解句流式 + 问题列表独立接口）
async function streamUnderstand(params) {
  const seq = ++requestSeq
  loading.value = true
  let accumulated = ''
  let questionsData = null
  let fieldsData = {}

  try {
    await Promise.all([
      // ① 理解句（流式）
      new Promise((resolve) => {
        streamUnderstanding({ ...params, scene: 'pre_analysis' }, {
          onSentence: (content) => {
            if (seq !== requestSeq) return
            accumulated = content
          },
          onDone: () => resolve(),
          onError: () => resolve()
        }).catch(() => resolve())
      }),
      // ② 问题列表（独立接口，格式纯净）
      getQuestions({ ...params, scene: 'pre_analysis' })
        .then((data) => {
          questionsData = data.questions || []
          fieldsData = data.extracted_fields || {}
        })
        .catch(() => {
          questionsData = []
        })
    ])

    if (seq !== requestSeq) return
    // 兜底：流式理解句为空（模型偶发空返回）时，给一句默认理解，避免空白
    if (!accumulated.trim()) {
      const head = rawQuery.value.replace(/^(怎么|如何|怎样|请问)/, '').slice(0, 20)
      accumulated = `我先理解为：针对「${head}」，我会先覆盖常见工程方向。`
    }
    understanding.value = {
      understanding_sentence: accumulated,
      extracted_fields: fieldsData
    }
    questions.value = questionsData
    answers.value = {}
    loading.value = false
    // 问题列表有则必答流程，无则直接出卡（兜底）
    if (questions.value.length === 0) {
      ensureCard()
    }
  } catch (e) {
    if (seq !== requestSeq) return
    loading.value = false
    ElMessage.warning('理解失败，请重试')
  }
}

async function fetchUnderstanding(params) {
  const seq = ++requestSeq
  loading.value = true
  try {
    const data = await understandProblem({ ...params, scene: 'pre_analysis' })
    if (seq !== requestSeq) return
    understanding.value = data
  } catch (e) {
    if (seq !== requestSeq) return
    ElMessage.warning('理解失败，请重试')
  } finally {
    if (seq === requestSeq) loading.value = false
  }
}

// 回答问题：记录答案（多选为数组；选「不确定」也算已答）
function handleAnswer({ field, value }) {
  answers.value = { ...answers.value, [field]: value }
}

// 把多选答案合并进 extracted_fields：
// - 标准字段里的数组型（constraints / uncertain_fields / search_directions）直接展开合并
// - 其他字段若是多选，用「、」拼成可读文本，避免下游拿到裸数组
const ARRAY_FIELDS = ['constraints', 'uncertain_fields', 'search_directions']
function mergeAnswers(baseFields = {}) {
  const merged = { ...baseFields }
  Object.entries(answers.value).forEach(([field, value]) => {
    if (!Array.isArray(value)) {
      merged[field] = value
      return
    }
    const items = value.filter(v => v !== undefined && v !== null && v !== '')
    if (items.length === 0) return
    if (ARRAY_FIELDS.includes(field)) {
      const existing = Array.isArray(merged[field]) ? merged[field] : []
      merged[field] = [...new Set([...existing, ...items])]
    } else {
      merged[field] = items.join('、')
    }
  })
  return merged
}

// 出卡后：把输入框内容同步成模型整理后的标准问题
function syncInputToStandard() {
  const standard = understanding.value?.understanding_card?.standard_problem
  if (standard && standard !== rawQuery.value) {
    rawQuery.value = standard
  }
}

// 出卡：确保有理解卡后展示（复用已有字段 + 已答问题，1 次调用）
async function ensureCard() {
  if (!understanding.value?.understanding_card) {
    const base = understanding.value || {}
    await fetchUnderstanding({
      raw_query: rawQuery.value,
      extracted_fields: mergeAnswers(base.extracted_fields || {}),
      understanding_sentence: base.understanding_sentence || '',
      suggestion: null
    })
  }
  syncInputToStandard()
  showCard.value = true
}

// 全部答完 → 显示「正在理解中」，合并答案生成卡
async function handleFinish() {
  const base = understanding.value || {}
  const extracted_fields = mergeAnswers(base.extracted_fields || {})
  const understanding_sentence = base.understanding_sentence || ''
  // 清空理解句，让理解条显示「正在理解中」，等卡算完再恢复
  understanding.value = null
  questions.value = []
  await fetchUnderstanding({
    raw_query: rawQuery.value,
    extracted_fields,
    understanding_sentence,
    suggestion: null
  })
  syncInputToStandard()
  showCard.value = true
}

// 跳过问题，直接生成理解卡
async function handleSkip() {
  questions.value = []
  answers.value = {}
  await ensureCard()
}

// 快捷键（Ctrl+Enter）或兜底入口：有未答问题先提示，否则出卡
async function handleStartClick() {
  if (!rawQuery.value) {
    ElMessage.warning('请先描述你的技术问题')
    return
  }
  if (rawQuery.value.length < MIN_LEN) {
    ElMessage.warning('再补充一点描述，一句话也可以')
    return
  }
  // 有未答问题也不阻塞：直接出理解卡，用户可后续补充
  await ensureCard()
}

// 「改一下理解」：清空旧理解显示「正在理解中」，强制系统重新理解并出一个问题列表
function handleRevise() {
  showCard.value = false
  understanding.value = null
  questions.value = []
  answers.value = {}
  streamUnderstand({ raw_query: rawQuery.value, force_suggestion: true })
}

function handleStart() {
  emit('start', buildPayload('standard'))
}

function handleQuickStart() {
  emit('quickStart', buildPayload('quick_first'))
}

// 组装传给下游的标准化对象（方案第 11 节）
function buildPayload(mode) {
  const f = understanding.value?.extracted_fields || {}
  const c = understanding.value?.understanding_card || {}
  return {
    raw_query: rawQuery.value,
    standard_problem: c.standard_problem || rawQuery.value,
    technical_object: f.technical_object || '',
    improvement_goal: f.improvement_goal || '',
    problem_phenomenon: f.problem_phenomenon || '',
    scenario: f.scenario || '',
    constraints: f.constraints || [],
    search_directions: c.search_directions || f.search_directions || [],
    uncertain_fields: c.uncertain_but_ok || f.uncertain_fields || [],
    analysis_mode: mode
  }
}

onBeforeUnmount(() => {
  requestSeq++ // 阻止卸载后的响应回写
})
</script>

<style scoped lang="scss">
.problem-understanding {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;

  &__bar {
    margin-top: 14px;
  }

  &__chips {
    margin-top: 12px;
  }

  &__card {
    margin-top: 16px;
  }
}
</style>
